import React, { useState, useEffect } from "react";
import OpenAI from "openai";
import "./style.css";
import PreviousAnalysis from "./components/previousAnalysis";
import { supabase } from "../../supabase";
import { Loader2, AlertCircle, FileX, ChevronDown, ChevronRight, MessageSquare, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import orcaLogo from "../../resources/orca-logo.png";
import Header from "../../components/Header";

const openai = new OpenAI({ apiKey: "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", dangerouslyAllowBrowser: true });

const Analysis = () => {
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [petType, setPetType] = useState("dog");
  const [language, setLanguage] = useState("english");
  // State variables for age, sex, and breed
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("male");
  const [breed, setBreed] = useState("");
  // State variable for X-ray area
  const [xrayArea, setXrayArea] = useState("TÓRAX");

  // trigger for redraw of previous
  const [trigger, setTrigger] = useState(0);

  // Fetch X-ray areas from Supabase
  const [xrayAreas, setXrayAreas] = useState([]);

  // Add state for prompts
  const [prompts, setPrompts] = useState([]);

  // Add state for prompt results
  const [promptResults, setPromptResults] = useState({});

  // Add state for active accordion items
  const [activeAccordionItems, setActiveAccordionItems] = useState({});

  // Add state for summary
  const [summary, setSummary] = useState("");

  // Add loading state for individual prompts
  const [promptLoading, setPromptLoading] = useState({});

  // Add state for prompts loading
  const [promptsLoading, setPromptsLoading] = useState(false);

  // Add state for summary prompt
  const [summaryPrompt, setSummaryPrompt] = useState(
    "Com base nas informações fornecidas, siga as instruções abaixo: 1. Para cada item analisado, monte uma tabela contendo os principais achados relevantes. Ignore áreas que contenham a indicação de “ausente”. 2. Elabore a seção 1Diagnósticos Diferenciais1: - Liste 2 a 3 hipóteses diagnósticas, priorizando as mais prováveis. - Justifique cada hipótese com base nos achados da imagem e nos sinais clínicos esperados. - Utilize bullet points para a listagem. 3. Elabore a seção 1Recomendações Clínicas e Próximos Passos1: - Descreva a conduta clínica imediata a ser tomada. - Sugira exames complementares necessários (ex: hemograma, bioquímica, ultrassonografia, tomografia, punções). - Classifique o grau de urgência, se aplicável. - Indique encaminhamento para especialidades se pertinente (ex: cirurgia, neurologia). 4. Elabore a seção 1Conduta e Tratamento Sugerido1: - Proponha uma terapia inicial, considerando medicações (anti-inflamatório, antibiótico, diurético etc.). - Informe o tempo de repouso, suporte clínico necessário ou indicação cirúrgica. - Oriente sobre cuidados pós-operatórios, se aplicável. - Sugira dieta, suporte nutricional ou restrições alimentares. - Indique posologia apenas se for possível com base nas informações recebidas. Organize a resposta de maneira clara, objetiva e estruturada, utilizando tabelas e bullet points sempre que necessário."
  );

  // Add PDF generation state
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const fetchAreas = async () => {
      const { data, error } = await supabase.from('areas').select('*');
      if (error) {
        console.error('Error fetching areas:', error);
      } else {
        setXrayAreas(data);
      }
    };
    fetchAreas();
  }, []);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles([...files, ...selectedFiles]);
  };

  const handleAsk = async () => {
    if (files.length === 0) return;
    setLoading(true);
    setResponse("");
    setSummary("");
  
    // Set all prompts to loading state
    const initialLoadingState = {};
    prompts.forEach(prompt => {
      initialLoadingState[prompt.id] = true;
    });
    setPromptLoading(initialLoadingState);
  
    try {
      // Convert all images to Base64
      const imagePromises = files.map(file => {
        return new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      });
  
      const imagesDataUrls = await Promise.all(imagePromises);
  
      // Run prompts on OpenAI
      const promptPromises = prompts.map(async prompt => {
        try {
          // Apply promptReplacements before sending to OpenAI
          const processedPromptText = promptReplacements(prompt.prompt);
          
          const processedSystemPromptText = promptReplacements(prompt.system_prompt);
          // Create content array with both text and images
          const content = [
            { type: "text", text: processedPromptText },
            ...imagesDataUrls.map(image => ({
              type: "image_url",
              image_url: { url: image, detail: "high" }
            }))
          ];
  
          const completion = await openai.chat.completions.create({
            model: "gpt-4.1-2025-04-14",
            messages: [
              {
                role: "system",
                content: processedSystemPromptText
              },
              {
                role: "user",
                content: content
              }
            ],
          });
          const result = completion.choices[0].message.content;
          console.log("🐞")
          console.log(result)
          // Update results immediately
          setPromptResults(prev => ({
            ...prev,
            [prompt.id]: result
          }));
          
          // Update loading state
          setPromptLoading(prev => ({
            ...prev,
            [prompt.id]: false
          }));
          
          return { id: prompt.id, content: result, success: true };
        } catch (error) {
          console.error(`Error running prompt ${prompt.id}:`, error);
          const errorMessage = `Error: ${error.message}`;
          
          // Update error in results immediately
          setPromptResults(prev => ({
            ...prev,
            [prompt.id]: errorMessage
          }));
          
          // Update loading state
          setPromptLoading(prev => ({
            ...prev,
            [prompt.id]: false
          }));
          
          return { id: prompt.id, content: errorMessage, success: false };
        }
      });

      // Wait for all prompts to finish for the summary
      const allResults = await Promise.all(promptPromises);
      const resultsMap = allResults.reduce((acc, result) => {
        acc[result.id] = result.content;
        return acc;
      }, {});
      
      // Generate summary once all results are in
      const summaryMessages = [
        { 
          role: "system", 
          content: "Você é um especialista em radiologia veterinária focado em análise crítica de resumos de laudos; receberá múltiplos resumos e deve identificar apenas informações técnicas relevantes, ignorar informações vagas ou genéricas, agrupar achados semelhantes, priorizar precisão, clareza e objetividade, sem inventar informações não fornecidas; responda com linguagem formal e técnica, organizando o resumo final de forma concisa em texto corrido ou listas curtas, evitando repetições." 
        }
      ];

      // Get the appropriate language prompt and apply replacements
      const currentSummaryPrompt = summaryPrompt
      
      // Create user message with text and images
      const userMessageContent = [
        { 
          type: "text", 
          text: promptReplacements(currentSummaryPrompt)
        }
      ];

      summaryMessages.push({
        role: "user",
        content: userMessageContent
      });

      // Add each prompt result as a separate message
      Object.entries(resultsMap).forEach(([promptId, content]) => {
        const summary = content.summary ?? content
        const promptName = prompts.find(p => p.id === parseInt(promptId))?.name || "Analysis";
        summaryMessages.push({ 
          role: "user", 
          content: `${promptName}: ${summary}`
        });
      });
      console.log("---------------")
      console.log(summaryMessages)
      console.log("---------------")
      const summaryCompletion = await openai.chat.completions.create({
        model: "gpt-4.1-2025-04-14",
        messages: summaryMessages,
      });
      const finalSummary = summaryCompletion.choices[0].message.content;
      setSummary(finalSummary);
      
      // Create a combined string of all prompts and their results
      let combinedPromptResults = "";
      
      // Add the summary prompt and result
      combinedPromptResults += "Summary Prompt:\n";
      combinedPromptResults += promptReplacements(currentSummaryPrompt);
      combinedPromptResults += "\n\nSummary Result:\n";
      combinedPromptResults += finalSummary;
      combinedPromptResults += "\n\n";
      
      // Add all individual prompts and their results
      Object.entries(resultsMap).forEach(([promptId, content]) => {
        const prompt = prompts.find(p => p.id === parseInt(promptId));
        if (prompt) {
          combinedPromptResults += `Prompt (${prompt.name}):\n`;
          combinedPromptResults += promptReplacements(prompt.prompt);
          combinedPromptResults += "\n\nResult:\n";
          combinedPromptResults += content;
          combinedPromptResults += "\n\n";
        }
      });
      
      // Save to database
      // await saveToDB(combinedPromptResults, imagesDataUrls, finalSummary);
      
    } catch (error) {
      console.error("Error fetching response:", error);
      setResponse(`Error fetching response: ${error.message}`);
    }

    setLoading(false);
  };

  const saveToDB = async (prompt, images, response) => {
    try {
      const { error } = await supabase
        .from('analysis-test')
        .insert({
          prompt: prompt,
          images: images,
          output: response
        });
        
      if (error) {
        console.error("Error saving to database:", error);
      } else {
        console.log("Analysis saved successfully");
        setTrigger((prevTrigger) => prevTrigger + 1);
      }
    } catch (err) {
      console.error("Exception when saving to database:", err);
    }
  }

  const removeFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const renderFormattedResponse = (content) => {
    if (!content) return null;
    
    // Convert markdown tables to HTML tables
    let formattedText = content;
    
    // Find markdown tables and convert them to HTML tables
    const tableRegex = /\|(.+)\|[\r\n]+\|([-:\s|]+)\|[\r\n]+((?:\|.+\|[\r\n]+)+)/g;
    formattedText = formattedText.replace(tableRegex, (match) => {
      // Split the table into rows
      const rows = match.split('\n').filter(row => row.trim() !== '');
      
      if (rows.length < 3) return match; // Not enough rows to be a proper table
      
      // Process the header row
      const headerRow = rows[0];
      const headerCells = headerRow.split('|').filter(cell => cell.trim() !== '');
      const headerHTML = `<tr>${headerCells.map(cell => `<th>${cell.trim()}</th>`).join('')}</tr>`;
      
      // Process the separator row (row 2) to determine alignment - skip for now
      
      // Process data rows
      const dataRowsHTML = rows.slice(2).map(row => {
        const cells = row.split('|').filter(cell => cell.trim() !== '');
        return `<tr>${cells.map(cell => `<td>${cell.trim()}</td>`).join('')}</tr>`;
      }).join('');
      
      return `<table class="markdown-table"><thead>${headerHTML}</thead><tbody>${dataRowsHTML}</tbody></table>`;
    });
    
    // Process other markdown elements
    formattedText = formattedText
      .replace(/#{1,6}\s+(.*)/g, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Process non-table lists
      .replace(/^- (.*)/gm, '<li>$1</li>')
      .replace(/\n\n/g, '<br/>');
    
    // Wrap consecutive list items in ul tags
    let inList = false;
    formattedText = formattedText.split('\n').map(line => {
      if (line.trim().startsWith('<li>')) {
        if (!inList) {
          inList = true;
          return `<ul>${line}`;
        }
        return line;
      } else if (inList && line.trim() !== '') {
        inList = false;
        return `</ul><p>${line}</p>`;
      } else if (inList && line.trim() === '') {
        inList = false;
        return `</ul>`;
      } else if (line.trim() && !line.startsWith('<h3>') && !line.startsWith('<table')) {
        return `<p>${line}</p>`;
      }
      return line;
    }).join('');
    
    // Close any open lists at the end
    if (inList) {
      formattedText += '</ul>';
    }
    
    // Add CSS for tables
    const tableStyles = `
      <style>
        .markdown-table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }
        .markdown-table th, .markdown-table td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        .markdown-table th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .markdown-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
      </style>
    `;
    
    return <div dangerouslySetInnerHTML={{ __html: tableStyles + formattedText }} />;
  };
  
  // Helper function to render input placeholders based on language
  const getPlaceholder = (field) => {
    const placeholders = {
      age: {
        english: "Enter age (e.g., 5 years, 6 months)",
        portuguese: "Inserir idade (ex: 5 anos, 6 meses)"
      },
      breed: {
        english: "Enter breed",
        portuguese: "Inserir raça"
      },
      details: {
        english: "Enter signs, history, or other relevant information...",
        portuguese: "Insira sinais, histórico ou outras informações relevantes..."
      }
    };
    
    return placeholders[field][language];
  };

  // Update the X-ray area dropdown change handler
  const handleXrayAreaChange = async (selectedAreaId) => {
    setPromptsLoading(true);
    setPrompts([]);
    
    try {
      // Fetch prompt IDs for the selected area
      const { data: areaPrompts, error: areaPromptsError } = await supabase
        .from('areas_prompts')
        .select('prompt_id')
        .eq('area_id', selectedAreaId);
      if (areaPromptsError) throw areaPromptsError;

      // Fetch prompt details using the prompt IDs
      const promptIds = areaPrompts.map(ap => ap.prompt_id);
      const { data: promptsData, error: promptsError } = await supabase
        .from('prompts')
        .select('*')
        .in('id', promptIds);
      if (promptsError) throw promptsError;
      console.log(promptsData)
      setPrompts(promptsData);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setPromptsLoading(false);
    }
  };

  // Add the promptReplacements function
  const promptReplacements = (promptText) => {
    if (!promptText) return promptText;
    
    // Determine sex display value based on language
    const sexDisplayValue = language === "english" 
      ? (sex === "male" ? "Male" : sex === "female" ? "Female" : "Unknown")
      : (sex === "male" ? "Macho" : sex === "female" ? "Fêmea" : "Desconhecido");
    
    // Determine pet type display value based on language
    const petTypeDisplayValue = language === "english" 
      ? petType 
      : (petType === "dog" ? "cachorro" : petType === "cat" ? "gato" : "outro");
    
    // Replace the placeholders with their respective values
    return promptText
      .replace(/{PET_TYPE}/g, petTypeDisplayValue)
      .replace(/{BREED}/g, breed || "Not specified")
      .replace(/{AGE}/g, age || "Not specified")
      .replace(/{SEX}/g, sexDisplayValue)
      .replace(/{ADDITIONAL_DETAILS}/g, additionalDetails);
  };

  // Update the toggle function for accordion
  const toggleAccordion = (promptId) => {
    setActiveAccordionItems(prev => ({
      ...prev,
      [promptId]: !prev[promptId]
    }));
  };

  // Add PDF generation function
  const handleGeneratePDF = async () => {
    if (!summary) return;
    
    setPdfLoading(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      let yPosition = 10;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const maxY = pageHeight - margin;
      
      // Helper function to check if we need a new page and add one if necessary
      const checkForNewPage = (expectedHeight) => {
        if (yPosition + expectedHeight > maxY) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };
      
      // Helper function to add text and handle overflow
      const addTextWithOverflow = (text, x, y, options = {}) => {
        // Split text into lines that fit the page width
        const textLines = pdf.splitTextToSize(text, pageWidth - 2 * x);
        
        // Calculate total height needed
        const lineHeight = options.lineHeight || 4;
        const totalHeight = textLines.length * lineHeight;
        
        // Iterate through lines and add them, creating new pages as needed
        let currentY = y;
        for (let i = 0; i < textLines.length; i++) {
          // Check if we need a new page
          if (currentY > maxY) {
            pdf.addPage();
            currentY = margin;
          }
          
          // Add the line
          pdf.text(textLines[i], x, currentY, options);
          currentY += lineHeight;
        }
        
        // Return the new Y position
        return currentY + (options.marginBottom || 2);
      };
      
      // Helper function to add styled text box
      const addStyledTextBox = (text, x, y, options = {}) => {
        const { bgColor = [240, 240, 240], textColor = [0, 0, 0], title = "" } = options;
        
        // Split text into lines that fit the page width
        const textWidth = pageWidth - 2 * x;
        const textLines = pdf.splitTextToSize(text, textWidth);
        
        // Calculate total height needed
        const lineHeight = options.lineHeight || 4;
        const padding = 3;
        const titleHeight = title ? 6 : 0;
        
        // Set text color and font
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...textColor);
        
        // Keep track of our starting y position
        let currentY = y;
        const startX = x;
        
        // Handle title if present
        if (title) {
          // Check if we need a new page for the title
          if (currentY + titleHeight > maxY) {
            pdf.addPage();
            currentY = margin;
          }
          
          // Draw background for title section
          pdf.setFillColor(...bgColor);
          pdf.roundedRect(startX - padding, currentY - padding, textWidth + padding * 2, titleHeight + padding * 2, 1, 1, 'F');
          
          // Add title
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(50, 50, 50);
          pdf.text(title, startX, currentY + 2);
          currentY += titleHeight + padding;
        }
        
        // Process text line by line
        let remainingLines = [...textLines];
        let isFirstSegment = true;
        
        while (remainingLines.length > 0) {
          // Calculate how many lines will fit on the current page
          const availableHeight = maxY - currentY;
          const linesPerPage = Math.floor(availableHeight / lineHeight);
          
          // If no lines will fit, move to next page
          if (linesPerPage <= 0) {
            pdf.addPage();
            currentY = margin;
            continue;
          }
          
          // Get lines for this page
          const linesToRender = remainingLines.slice(0, linesPerPage);
          remainingLines = remainingLines.slice(linesPerPage);
          
          // Calculate height for this segment
          const segmentHeight = linesToRender.length * lineHeight + (isFirstSegment ? 0 : padding);
          
          // Draw background for this segment
          pdf.setFillColor(...bgColor);
          
          // Draw background - different corners rounded based on position
          if (isFirstSegment && remainingLines.length === 0) {
            // Single segment box (all corners rounded)
            pdf.roundedRect(startX - padding, currentY - padding, textWidth + padding * 2, segmentHeight + padding, 1, 1, 'F');
          } else if (isFirstSegment) {
            // Top segment (top corners rounded)
            pdf.roundedRect(startX - padding, currentY - padding, textWidth + padding * 2, segmentHeight + padding, 1, 1, 'F');
          } else if (remainingLines.length === 0) {
            // Bottom segment (bottom corners rounded)
            pdf.roundedRect(startX - padding, currentY - padding, textWidth + padding * 2, segmentHeight + padding, 1, 1, 'F');
          } else {
            // Middle segment (no corners rounded - just a rectangle)
            pdf.rect(startX - padding, currentY - padding, textWidth + padding * 2, segmentHeight + padding, 'F');
          }
          
          // Add the text
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...textColor);
          
          for (let i = 0; i < linesToRender.length; i++) {
            pdf.text(linesToRender[i], startX, currentY + i * lineHeight);
          }
          
          // Update current Y position
          currentY += segmentHeight;
          
          // No longer the first segment
          isFirstSegment = false;
          
          // If we have more lines and we're at the end of the page, add a new page
          if (remainingLines.length > 0 && currentY >= maxY - lineHeight) {
            pdf.addPage();
            currentY = margin;
          }
        }
        
        return currentY + padding;
      };
      
      // Add title only - no logo
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text("ORCA", pageWidth / 2, yPosition + 10, { align: 'center' });
      
      yPosition += 20; // Reduced from 32 since we're not showing the logo
      
      // Add line
      pdf.setDrawColor(200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
      
      // Add patient information
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text("Informação do paciente:", margin, yPosition);
      yPosition += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      
      // Determine pet type display value based on language
      const petTypeDisplayValue = language === "english" 
        ? petType 
        : (petType === "dog" ? "cachorro" : petType === "cat" ? "gato" : "outro");
      
      // Determine sex display value based on language
      const sexDisplayValue = language === "english" 
        ? (sex === "male" ? "Male" : sex === "female" ? "Female" : "Unknown")
        : (sex === "male" ? "Macho" : sex === "female" ? "Fêmea" : "Desconhecido");
      
      // Add patient details
      pdf.text(`Tipo: ${petTypeDisplayValue}`, 15, yPosition);
      yPosition += 6;
      pdf.text(`Raça: ${breed || "Not specified"}`, 15, yPosition);
      yPosition += 6;
      pdf.text(`Idade: ${age || "Not specified"}`, 15, yPosition);
      yPosition += 6;
      pdf.text(`Sexo: ${sexDisplayValue}`, 15, yPosition);
      yPosition += 6;
      
      // Find selected X-ray area name
      const selectedArea = xrayAreas.find(area => area.id == xrayArea);
      pdf.text(`Area: ${selectedArea ? selectedArea.name : xrayArea}`, 15, yPosition);
      
      yPosition += 12;
      
      checkForNewPage(10);
      
      // Add line
      pdf.setDrawColor(200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
      
      // Add each prompt result
      for (const prompt of prompts) {
        checkForNewPage(20);
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${prompt.name}:`, margin, yPosition);
        yPosition += 8;
        
        checkForNewPage(15);
        
        // Add system prompt with styled background (light blue)
        if (prompt.system_prompt) {
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          yPosition = addStyledTextBox(
            promptReplacements(prompt.system_prompt) || "No system prompt available", 
            15, 
            yPosition + 4, 
            { 
              bgColor: [230, 242, 255], 
              textColor: [70, 70, 70],
              title: "System Prompt:"
            }
          );
          yPosition += 4; // Add extra space after the system prompt box
        }
        
        checkForNewPage(15);
        
        // Add user prompt with styled background (light gray)
        pdf.setFontSize(9);
        yPosition = addStyledTextBox(
          promptReplacements(prompt.prompt) || "No prompt available", 
          15, 
          yPosition, 
          { 
            bgColor: [240, 240, 240], 
            textColor: [70, 70, 70],
            title: "Prompt:"
          }
        );
        yPosition += 4; // Add extra space after the prompt box
        
        checkForNewPage(15);
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text("Resultado:", 15, yPosition);
        yPosition += 5;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        if (promptResults[prompt.id]) {
          // Create a clean text version of the result (remove HTML)
          const div = document.createElement('div');
          div.innerHTML = promptResults[prompt.id];
          const cleanText = div.textContent || div.innerText || "No result available";
          
          yPosition = addTextWithOverflow(cleanText, 15, yPosition, { lineHeight: 4, marginBottom: 10 });
        } else {
          pdf.text("No result available", 15, yPosition);
          yPosition += 10;
        }
        
        checkForNewPage(10);
        
        // Add separator
        pdf.setDrawColor(200);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;
      }
      
      checkForNewPage(20);
      
      // Add summary
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text("Relatorio final:", margin, yPosition);
      yPosition += 8;
      
      checkForNewPage(15);
      
      // Add summary prompt with styled background
      pdf.setFontSize(9);
      yPosition = addStyledTextBox(
        promptReplacements(summaryPrompt) || "No summary prompt available", 
        15, 
        yPosition, 
        { 
          bgColor: [240, 240, 240], 
          textColor: [70, 70, 70],
          title: "Prompt:"
        }
      );
      yPosition += 4; // Add extra space after the prompt box
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text("Resultado:", 15, yPosition);
      yPosition += 5;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      
      // Create a clean text version of the summary (remove HTML)
      const div = document.createElement('div');
      div.innerHTML = summary || "";
      const cleanSummary = div.textContent || div.innerText || "No summary available";
      
      addTextWithOverflow(cleanSummary, 15, yPosition, { lineHeight: 4 });
      
      // Add page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Página ${i} de ${pageCount}`, pageWidth - 20, pageHeight - 5);
      }
      
      // Save the PDF
      pdf.save(`xray-analysis-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header title="Analysis" />
      
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="vet-ai-container">
        
        <div className="vet-ai-layout">
          {/* Left panel - Image list */}
          <div className="vet-ai-image-list">
            <div className="upload-container">
              <label className="upload-button">
                <input
                  type="file"
                  multiple
                  accept="image/png, image/jpeg, image/gif"
                  onChange={handleFileChange}
                  className="vet-ai-file-input"
                />
              <span>+ Add X-ray Images</span>
            </label>
          </div>
          
          <div className="image-list-container">
            {files.map((file, index) => (
              <div 
                key={index} 
                className={`image-list-item`}
              >
                <img 
                  src={URL.createObjectURL(file)} 
                  alt={`x-ray-${index}`} 
                  className="thumbnail" 
                />
                <button 
                  className="remove-image-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
        
        {/* Middle panel - Active image and analysis button */}
        <div className="vet-ai-main-content">
          
          <div className="vet-ai-controls">
            {/* Add language selector */}
            <div className="form-group">
              <label>Language / Idioma:</label>
              <div className="vet-ai-radio-group">
                <label>
                  <input
                    type="radio"
                    value="english"
                    checked={language === "english"}
                    onChange={() => setLanguage("english")}
                  />
                  English
                </label>
                <label>
                  <input
                    type="radio"
                    value="portuguese"
                    checked={language === "portuguese"}
                    onChange={() => setLanguage("portuguese")}
                  />
                  Português
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>{language === "english" ? "Pet Type:" : "Tipo de Animal:"}</label>
              <div className="vet-ai-radio-group">
                <label>
                  <input
                    type="radio"
                    value="dog"
                    checked={petType === "dog"}
                    onChange={() => setPetType("dog")}
                  />
                  {language === "english" ? "Dog" : "Cachorro"}
                </label>
                <label>
                  <input
                    type="radio"
                    value="cat"
                    checked={petType === "cat"}
                    onChange={() => setPetType("cat")}
                  />
                  {language === "english" ? "Cat" : "Gato"}
                </label>
                <label>
                  <input
                    type="radio"
                    value="other"
                    checked={petType !== "dog" && petType !== "cat"}
                    onChange={() => setPetType("other")}
                  />
                  {language === "english" ? "Other" : "Outro"}
                </label>
              </div>
            </div>
            
            {/* Inputs for age, sex, and breed */}
            <div className="form-group">
              <label>{language === "english" ? "Age:" : "Idade:"}</label>
              <input
                type="text"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder={getPlaceholder("age")}
                className="vet-ai-input"
              />
            </div>
            
            <div className="form-group">
              <label>{language === "english" ? "Sex:" : "Sexo:"}</label>
              <div className="vet-ai-radio-group">
                <label>
                  <input
                    type="radio"
                    value="male"
                    checked={sex === "male"}
                    onChange={() => setSex("male")}
                  />
                  {language === "english" ? "Male" : "Macho"}
                </label>
                <label>
                  <input
                    type="radio"
                    value="female"
                    checked={sex === "female"}
                    onChange={() => setSex("female")}
                  />
                  {language === "english" ? "Female" : "Fêmea"}
                </label>
                <label>
                  <input
                    type="radio"
                    value="unknown"
                    checked={sex === "unknown"}
                    onChange={() => setSex("unknown")}
                  />
                  {language === "english" ? "Unknown" : "Desconhecido"}
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>{language === "english" ? "Breed:" : "Raça:"}</label>
              <input
                type="text"
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                placeholder={getPlaceholder("breed")}
                className="vet-ai-input"
              />
            </div>
            
            {/* X-ray area dropdown */}
            <div className="form-group">
              <label>{language === "english" ? "X-ray Area:" : "Área do Raio-X:"}</label>
              <select
                value={xrayArea}
                onChange={(e) => {
                  setXrayArea(e.target.value);
                  handleXrayAreaChange(e.target.value);
                }}
                className="vet-ai-input"
              >
                {xrayAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Additional Details:</label>
              <textarea
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder={getPlaceholder("details")}
                className="vet-ai-input"
                rows="3"
              />
            </div>
            
            <button 
              onClick={handleAsk} 
              disabled={loading || prompts.length === 0} 
              className="vet-ai-button flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5" />
                  <span>{language === "english" ? "Analyzing..." : "Analisando..."}</span>
                </>
              ) : (
                language === "english" ? "Analyze X-ray" : "Analisar Raio-X"
              )}
            </button>
          </div>
        </div>
        
        {/* Right panel - Analysis results */}
        <div className="vet-ai-results">
          {promptsLoading ? (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-center h-40">
              <div className="flex flex-col items-center gap-3 text-gray-600">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
                <span>Loading prompts...</span>
              </div>
            </div>
          ) : prompts.length > 0 ? (
            <div className="vet-ai-response">
              <div className="divide-y divide-gray-200 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                {prompts.map((prompt, index) => (
                  <div key={index} className="bg-white">
                    <button 
                      className={`w-full p-4 flex items-center justify-between font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 ${activeAccordionItems[prompt.id] ? 'bg-gray-50' : ''}`} 
                      onClick={() => toggleAccordion(prompt.id)}
                    >
                      <div className="flex items-center gap-3">
                        <span>{prompt.name}</span>
                      </div>
                      {activeAccordionItems[prompt.id] ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-200 ${activeAccordionItems[prompt.id] ? 'max-h-96 overflow-y-auto' : 'max-h-0'}`}
                    >
                      <div className="p-4 border-t border-gray-100 bg-gray-50">
                        {/* Display the system prompt with replacements */}
                        {prompt.system_prompt && (
                          <div className="prompt-preview mb-3 p-2 bg-blue-50 rounded text-sm text-gray-700 border border-blue-200">
                            <strong>System Prompt:</strong> {promptReplacements(prompt.system_prompt)}
                          </div>
                        )}
                        {/* Display the user prompt with replacements */}
                        <div className="prompt-preview mb-3 p-2 bg-gray-100 rounded text-sm text-gray-700">
                          <strong>Prompt:</strong> {promptReplacements(prompt.prompt)}
                        </div>
                        {promptLoading[prompt.id] ? (
                          <div className="flex items-center gap-2 text-gray-600 p-4">
                            <Loader2 className="animate-spin h-5 w-5 text-blue-500" />
                            <span>Processing prompt...</span>
                          </div>
                        ) : promptResults[prompt.id]?.startsWith('Error:') ? (
                          <div className="flex items-start gap-2 text-red-600 p-2 bg-red-50 rounded">
                            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            <span>{promptResults[prompt.id]}</span>
                          </div>
                        ) : promptResults[prompt.id] ? (
                          <div className="prose max-w-none text-gray-700">
                            {renderFormattedResponse(promptResults[prompt.id])}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-gray-500 p-4">
                            <FileX className="h-5 w-5" />
                            <span>No result available</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-results-placeholder bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <MessageSquare className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Select an X-ray area to view available prompts.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add summary panel */}
      <div className="mt-4 p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Relátorio final</h2>
          
          {/* Replace button with just an icon */}
          {summary && (
            <div className="flex items-center">
              {pdfLoading ? (
                <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
              ) : (
                <Download 
                  className="h-6 w-6 text-blue-600 hover:text-blue-800 cursor-pointer transition-colors" 
                  onClick={handleGeneratePDF}
                  title={language === "english" ? "Download PDF" : "Baixar PDF"}
                />
              )}
            </div>
          )}
        </div>
        
        <div className="mb-4">
          <label htmlFor="summaryPrompt" className="block mb-2 text-sm font-medium text-gray-700">
            {language === "english" ? "Summary Prompt:" : "Prompt do Resumo:"}
          </label>
          <div className="mb-3 p-3 bg-gray-100 rounded border border-gray-300 text-sm text-gray-700 max-h-64 overflow-y-auto">
            {renderFormattedResponse(promptReplacements(summaryPrompt))}
          </div>
          <textarea
            id="summaryPrompt"
            value={summaryPrompt}
            onChange={(e) => setSummaryPrompt(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows="5"
            placeholder={language === "english" ? "Enter prompt for generating the summary..." : "Insira o prompt para gerar o resumo..."}
          ></textarea>
        </div>
        
        {loading ? (
          <div className="flex items-center gap-2 text-gray-600">
            <Loader2 className="animate-spin h-5 w-5" />
            <span>{language === "english" ? "Generating summary..." : "Gerando resumo..."}</span>
          </div>
        ) : summary ? (
          <div className="prose max-w-none">
            {renderFormattedResponse(summary)}
          </div>
        ) : (
          <p className="text-gray-500">
            {language === "english" ? "No summary available yet." : "Nenhum resumo disponível ainda."}
          </p>
        )}
          </div>
        </div>

        <PreviousAnalysis trigger={trigger}/>
      </div>
    </div>
  );
};

export default Analysis;